import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UserPostsList from '@/redux/post/UserPostsList';
import PostCard from '@/components/Post/PostCard';
import { useGetProfileFeedQuery } from '@/redux/post/post.api.slice';
import {
  useGetBookmarksQuery,
  useGetBookmarkedProjectGroupsQuery,
} from '@/redux/bookmark/bookmark.api.slice';
import {
  IPostsSort,
  IPostVisibility,
  IPostWithUserAndAttachments,
} from '@/redux/post/post';
import { IProjectGroupCardItem } from '@/redux/projectGroup/projectGroup.d';
import { IFeedItem } from '@/redux/feed/feed.d';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import DownloadLogsTable from '@/redux/downloadLog/DownloadLogsTable';
import ProjectGroupCard from '@/components/ProjectGroup/ProjectGroupCard';
import { Skeleton } from '@/components/ui/skeleton';
import { getDisplayName } from '@/lib/utils';
import { POSTS_PAGE_LIMIT, SORT_OPTIONS } from '@/redux/post/post.utils';

interface ProfileContentViewProps {
  username: string;
  currentUserId: string | null;
}

/**
 * Component for displaying bookmarked posts and project groups
 */
function BookmarksView({
  currentUserId,
  filteredPosts,
  filteredProjectGroups,
  isLoading,
}: {
  currentUserId: string | null;
  filteredPosts: IPostWithUserAndAttachments[];
  filteredProjectGroups: IProjectGroupCardItem[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-2 w-full">
        {Array.from({ length: 12 }).map((_, idx) => (
          <Skeleton key={`skeleton-${idx}`} className="w-full h-[420px]" />
        ))}
      </div>
    );
  }

  if (filteredPosts.length === 0 && filteredProjectGroups.length === 0) {
    return (
      <div className="flex items-center justify-center h-[500px]">
        <p className="text-sm text-gray-500">No bookmarks found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-2 w-full">
      {filteredProjectGroups
        .filter((group) => group !== null && group !== undefined)
        .map((group) => (
          <ProjectGroupCard
            key={group._id}
            id={group._id}
            name={group.name}
            description={group.description}
            thumbnail={group.thumbnail}
            likesCount={group.likesCount}
            commentsCount={group.commentsCount}
            createdAt={group.createdAt}
            hasLikedByUser={group.hasLikedByUser}
            hasBookmarkedByUser={group.hasBookmarkedByUser}
            isOwner={
              typeof group.user === 'string'
                ? group.user === currentUserId
                : group.user._id === currentUserId
            }
            authorUsername={
              typeof group.user === 'string' ? '' : group.user.username
            }
            authorName={
              typeof group.user === 'string' ? '' : getDisplayName(group.user)
            }
            authorAvatar={
              typeof group.user === 'string' ? '' : group.user.profilePicture
            }
            visibility={group.visibility}
            isOnefinityOfficial={
              typeof group.user === 'string'
                ? false
                : group.user.isOnefinityOfficial
            }
            isTrustedMember={
              typeof group.user === 'string'
                ? false
                : group.user.isTrustedMember
            }
            partnerData={group.partner}
            category={group.category}
            difficulty={group.difficulty}
            averageTotalRating={group.averageTotalRating}
            totalMakes={group.totalMakes}
            materialLinks={group.materialLinks}
            materialUsed={group.materialUsed}
            youtubeUrl={group.youtubeUrl}
          />
        ))}
      {filteredPosts
        .filter((post) => post !== null && post !== undefined)
        .map((post) => (
          <PostCard
            key={post._id}
            id={post._id}
            authorName={getDisplayName(post.user)}
            authorAvatar={post.user.profilePicture}
            authorUsername={post.user.username}
            title={post.title}
            likesCount={post.likes}
            commentsCount={post.comments}
            postThumbnail={post.postThumbnail ?? null}
            downloadCount={post.downloadCount ?? 0}
            createdAt={post.createdAt}
            category={post.category}
            difficulty={post.difficulty}
            materialUsed={post.materialUsed}
            description={post.description}
            hasLikedByUser={post.hasLikedByUser}
            isOnefinityOfficial={post.user.isOnefinityOfficial}
            isTrustedMember={post.user.isTrustedMember}
            isOwner={post.user._id === currentUserId}
            visibility={post.visibility}
            partnerData={post?.partner}
            hasBookmarkedByUser={post?.hasBookmarkedByUser}
            averageTotalRating={post.averageTotalRating}
            totalMakes={post.totalMakes}
            youtubeUrl={post.youtubeUrl}
            materialLink={post.materialLink}
            materialLinks={post.materialLinks}
          />
        ))}
    </div>
  );
}

/**
 * Keyset cursor that auto-resets to the first page whenever `resetKey` changes.
 * Used for the URL-derived status tab (`postType`): unlike the local search/sort
 * state it can't reset the cursor in the same setState batch, so we reset
 * synchronously during render (React's "adjust state on prop change" pattern) —
 * the query then never fires with a stale cursor against the new tab's cache key.
 */
function useResettableCursor(resetKey: string | undefined) {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [prevKey, setPrevKey] = useState(resetKey);
  if (resetKey !== prevKey) {
    setPrevKey(resetKey);
    setCursor(undefined);
  }
  return [cursor, setCursor] as const;
}

export default function ProfileContentView({
  username,
  currentUserId,
}: ProfileContentViewProps) {
  const { getSearchParam } = useSearchParamActions();
  const postType = getSearchParam('postType');
  const view = getSearchParam('view');

  // These three tabs render the owner's posts/groups list.
  const isUserPostsView = view === 'userPosts' || view === undefined;
  const isBookmarksView = view === 'bookmarks';

  // Search + sort are driven from the navbar's chrome row, so they travel in the
  // URL rather than local state (ProfileSearchRow debounces the writes).
  const search = getSearchParam('search') ?? '';
  const sort = (getSearchParam('sort') ?? SORT_OPTIONS[0].value) as IPostsSort;
  // Keyset cursor (`undefined` = first page). Every one of search/sort/tab
  // changes the query context, so all three reset it.
  const [cursor, setCursor] = useResettableCursor(
    `${postType ?? ''}|${search}|${sort}`
  );

  const {
    data: feedData,
    isLoading: isFeedLoading,
    isFetching: isFeedFetching,
  } = useGetProfileFeedQuery(
    {
      username,
      cursor,
      limit: POSTS_PAGE_LIMIT,
      search,
      sort,
      // The status tabs (Draft / Published / Pending / Required Edits) are sent
      // as a server-side `visibility` filter so narrowing spans ALL pages (posts
      // AND groups), not just the pages already loaded. Omitted when undefined.
      visibility: postType as IPostVisibility | undefined,
    },
    { skip: !username || !isUserPostsView }
  );

  // Bookmark queries only run on the Bookmarks tab.
  const { data: bookmarkedPosts, isLoading: isBookmarkedPostsLoading } =
    useGetBookmarksQuery(undefined, { skip: !isBookmarksView });
  const {
    data: bookmarkedProjectGroups,
    isLoading: isBookmarkedProjectGroupsLoading,
  } = useGetBookmarkedProjectGroupsQuery(undefined, { skip: !isBookmarksView });

  // One query now returns posts AND project groups interleaved, keyset-paginated
  // by the DB — no separate groups call, no client-side merging or filtering.
  const accumulatedItems: IFeedItem[] = useMemo(
    () => feedData?.items ?? [],
    [feedData]
  );
  const hasMore = feedData?.hasMore ?? false;
  const nextCursor = feedData?.nextCursor ?? null;

  // Keep nextCursor in a ref so onLoadMore stays stable and always reads the
  // freshest value (avoids re-creating the callback on every page).
  const nextCursorRef = useRef<string | null>(nextCursor);
  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  const handleLoadMore = useCallback(async () => {
    if (nextCursorRef.current) {
      // Setting the cursor changes the query arg -> forceRefetch fetches the
      // next page, which `merge` appends to the accumulated list.
      setCursor(nextCursorRef.current);
    }
  }, [setCursor]);

  switch (view) {
    case 'bookmarks':
      return (
        <BookmarksView
          currentUserId={currentUserId}
          filteredPosts={bookmarkedPosts ?? []}
          filteredProjectGroups={bookmarkedProjectGroups ?? []}
          isLoading={
            isBookmarkedPostsLoading || isBookmarkedProjectGroupsLoading
          }
        />
      );

    case 'downloadLogs':
      return <DownloadLogsTable />;

    default:
      return (
        <div className="flex flex-col gap-4 w-full">
          <UserPostsList
            userId={currentUserId ?? ''}
            items={accumulatedItems}
            isLoading={isFeedLoading && accumulatedItems.length === 0}
            hasMore={hasMore}
            isFetchingMore={isFeedFetching && accumulatedItems.length > 0}
            onLoadMore={handleLoadMore}
          />
        </div>
      );
  }
}
